<template>
  <div class="left-panel">
    <h2>Control Panel</h2>
    
    <div class="panel-section">
      <div class="section-header">
        <h3>Map Controls</h3>
      </div>
      <div class="map-controls">
        <button 
          @click="toggleDepotMode" 
          :class="{ 'active': depotMode }"
          class="control-btn"
        >
          {{ depotMode ? 'Setting Depot' : 'Set Depot Location' }}
        </button>
        <button 
          @click="toggleLocationCreationMode" 
          :class="{ 'active': locationCreationMode }"
          class="control-btn"
        >
          {{ locationCreationMode ? 'Adding Locations' : 'Add Locations by Click' }}
        </button>
        <button 
          @click="clearMap" 
          class="control-btn"
        >
          Clear Map
        </button>
      </div>
    </div>
    
    <div class="panel-section">
      <div class="section-header">
        <h3>{{ locationsSectionTitle }}</h3>
      </div>
      <div class="map-controls">
        <button 
          @click="toggleLocations" 
          :class="{ 'active': showLocations }"
          class="control-btn"
        >
          {{ showLocations ? 'Hide Locations' : 'Show Locations' }}
        </button>
      </div>
    </div>
    
    <div class="panel-section" v-if="selectedLocationsCount > 0">
      <div class="section-header">
        <h3>Selected Locations</h3>
      </div>
      <div class="selection-info">
        <p>{{ selectedLocationsCount }} location(s) selected via map drawing</p>
        <div class="map-controls">
          <button 
            @click="clearSelection" 
            class="control-btn"
          >
            Clear Selection
          </button>
        </div>
      </div>
    </div>
    
    <div class="panel-section">
      <RoutingPanel 
        ref="routingPanel"
        @solution-found="handleSolutionFound"
        @location-source-changed="handleLocationSourceChanged"
      />
    </div>
  </div>
</template>

<script>
import RoutingPanel from '@/components/RoutingPanel.vue';

export default {
  name: 'LeftPanel',
  components: {
    RoutingPanel
  },
  data() {
    return {
      depotMode: false,
      showLocations: false,
      locationCreationMode: false,
      selectedLocationsCount: 0
    };
  },
  computed: {
    locationsSectionTitle() {
      // Get the current location source from the routing panel
      const routingPanel = this.$refs.routingPanel;
      if (routingPanel) {
        const locationSource = routingPanel.locationSource;
        const selectedCollectionId = routingPanel.selectedCollectionId;
        const availableCollections = routingPanel.availableCollections;
        
        if (locationSource === 'collections' && selectedCollectionId) {
          const collection = availableCollections.find(c => c.id === selectedCollectionId);
          return collection ? `Collection: ${collection.name}` : 'Collection Locations';
        } else if (locationSource === 'collections') {
          return 'Collection Locations';
        } else if (locationSource === 'database') {
          return 'Database Locations';
        }
      }
      return 'Database Locations'; // Default fallback
    }
  },
  methods: {
    /**
     * Toggle between adding stops and setting the depot
     */
    toggleDepotMode() {
      this.depotMode = !this.depotMode;
      this.$emit('depot-mode-changed', this.depotMode);
    },
    
    /**
     * Clear all map data
     */
    clearMap() {
      this.$refs.routingPanel.clearLocations();
      this.$emit('clear-map');
    },
    
    /**
     * Handle when the user sets a depot location
     */
    setDepot(coordinates) {
      this.$refs.routingPanel.setDepot(coordinates);
    },
    
    /**
     * Handle when the user adds a location
     */
    addLocation(coordinates) {
      this.$refs.routingPanel.addLocation(coordinates);
    },
    
    /**
     * Handle when a solution is found
     */
    handleSolutionFound(data) {
      this.$emit('solution-found', data);
    },
    
    /**
     * Handle when location source changes
     */
    handleLocationSourceChanged(data) {
      this.$emit('location-source-changed', data);
    },
    
    /**
     * Toggle locations visibility
     */
    toggleLocations() {
      this.showLocations = !this.showLocations;
      this.$emit('locations-visibility-changed', this.showLocations);
    },
    
    /**
     * Toggle location creation mode
     */
    toggleLocationCreationMode() {
      this.locationCreationMode = !this.locationCreationMode;
      // Disable depot mode when enabling location creation mode
      if (this.locationCreationMode && this.depotMode) {
        this.depotMode = false;
        this.$emit('depot-mode-changed', this.depotMode);
      }
      this.$emit('location-creation-mode-changed', this.locationCreationMode);
    },
    
    /**
     * Handle selected locations from map polygon/rectangle selection
     */
    handleSelectedLocations(locations) {
      // Update selected locations count
      this.selectedLocationsCount = locations ? locations.length : 0;
      
      // Pass selected locations to routing panel
      this.$refs.routingPanel.addSelectedLocations(locations);
    },
    
    /**
     * Clear the current selection
     */
    clearSelection() {
      this.selectedLocationsCount = 0;
      // Clear selection in routing panel to ensure complete state reset
      if (this.$refs.routingPanel) {
        this.$refs.routingPanel.clearSelectedLocations();
      }
      this.$emit('clear-selection');
    },
    
    /**
     * Handle boundary selection from map
     */
    handleBoundarySelected(boundary) {
      console.log('Boundary selected in LeftPanel:', boundary);
      // Pass boundary info to RoutingPanel if needed
      if (this.$refs.routingPanel) {
        this.$refs.routingPanel.handleBoundarySelected(boundary);
      }
    },
    
    /**
     * Handle locations filtered by boundary selection
     */
    handleLocationsBoundaryFiltered(filteredLocations) {
      console.log('Locations filtered by boundary in LeftPanel:', filteredLocations?.length || 'all');
      
      if (filteredLocations && filteredLocations.length > 0) {
        // Use the existing method to add selected locations to routing panel
        this.handleSelectedLocations(filteredLocations);
        
        // Update selected locations count for display
        this.selectedLocationsCount = filteredLocations.length;
        
        console.log(`Auto-selected ${filteredLocations.length} locations from boundary for routing`);
      } else {
        // Clear selection when no boundary is selected
        this.clearSelection();
      }
    }
  }
}
</script>

<style scoped>
.left-panel {
  background-color: #2c3e50;
  background-image: linear-gradient(135deg, rgba(0, 0, 0, 0.05) 25%, transparent 25%), 
                    linear-gradient(225deg, rgba(0, 0, 0, 0.05) 25%, transparent 25%), 
                    linear-gradient(45deg, rgba(0, 0, 0, 0.05) 25%, transparent 25%), 
                    linear-gradient(315deg, rgba(0, 0, 0, 0.05) 25%, transparent 25%);
  background-position: 10px 0, 10px 0, 0 0, 0 0;
  background-size: 20px 20px;
  background-repeat: repeat;
  color: white;
  height: 100%;
  width: 100%;
  padding: 1.5rem;  /* Increased padding for better spacing */
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

h2 {
  margin-top: 0;
  font-size: 1.3rem;
  margin-bottom: 0.75rem;
  padding-left: 0.5rem;  /* Added left padding */
}

.panel-section {
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  margin-bottom: 0.75rem;
  overflow: visible;
}

.section-header {
  padding: 0.5rem 1.5rem;  /* Increased left padding */
  background-color: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.section-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

.map-controls {
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.5rem;  /* Increased left padding */
  flex-wrap: wrap;
}

.control-btn {
  padding: 0.5rem 1rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;
  flex: 1;
  min-width: 100px;
}

.control-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.control-btn.active {
  background-color: #4285F4;
}

.selection-info {
  padding: 1rem 1.5rem;
}

.selection-info p {
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
  color: #ecf0f1;
}
</style>
